import React, {memo, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {getIsSearchPending} from 'modules/sapiSearch/selectors'
import {FilterUrlParam, FilterUrlParams} from 'modules/search/types'
import {FacetType} from 'types/Search'

import {Box} from '@vio/atlas/helpers/Box'
import {cssTheme} from '@vio/atlas/themes'

import {filterMessages} from '../filterMessages'
import {getValidFacetGroups, getValidFacets} from '../utils'
import {FacetFilterContentLoader} from './FacetFilterContentLoader'
import {FacetsRenderCard} from './FacetsRenderCard'
import {FacetsRenderCheckboxList} from './FacetsRenderCheckboxList'
import {FacetsRenderGroup} from './FacetsRenderGroup'
import {FacetsRenderList} from './FacetsRenderList'
import {FacetsRenderPills} from './FacetsRenderPills/FacetsRenderPills'
import {
  ColumnType,
  FacetGroupItem,
  RenderType,
  ShowMoreRenderType
} from './types'

const LENGTH_FOR_POPULAR_LIST_DISPLAY = 9
const MAX_LIST_HEIGHT = 260

const ScrollableWrapper = styled.div`
  width: 100%;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  &.--is-popover {
    width: 400px;
  }
  &.--is-bottom-sheet {
    max-height: 100%;
    overflow-y: hidden;
    overflow-x: hidden;
  }
`

const ScrollableListWrapper = styled.div<{
  maxHeight: number
  renderType: RenderType
}>`
  display: flex;
  width: 364px;
  max-height: ${({maxHeight = MAX_LIST_HEIGHT}) => maxHeight}px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: ${cssTheme.layout.spacing.s100};
  padding-left: ${({renderType}) =>
    cssTheme.layout.spacing[renderType === RenderType.Card ? 's100' : 's300']};
  padding-right: ${({renderType}) =>
    cssTheme.layout.spacing[renderType === RenderType.Card ? 's400' : 's300']};
  padding-bottom: ${cssTheme.layout.spacing.s700};

  > button:not(:last-child) {
    margin-right: ${cssTheme.layout.spacing.s300};
  }
`

const ListWrapper = styled.div<{maxHeight?: number}>`
  display: flex;
  max-height: ${({maxHeight}) => (maxHeight ? `${maxHeight}px` : 'auto')};
  > button:not(:last-child) {
    margin-right: ${cssTheme.layout.spacing.s300};
  }
`

export interface FacetFilterProps {
  /** The list of facets for this filter */
  facets: FacetType[]
  /** Group of facets to be displayed */
  facetGroups: FacetGroupItem[]
  /** The filters applied in local state */
  filters: FilterUrlParam
  /** The filters applied from the search params */
  appliedFilters: FilterUrlParam
  /** The search param name for this filter */
  searchParamName: string
  /** The callback to render the label */
  getLabel: (T: string) => string | unknown
  /** The callback fired on filter change */
  onChange: (filterName: string, filterValue: string | null | undefined) => void
  /** The callback fired on set multi filters */
  setMultiFilters?: (
    filterName: string,
    filterValues: (string | number)[]
  ) => void
  /** The callback fired on remove multi filters */
  removeMultiFilters?: (
    filterName: string,
    filterValues: (string | number)[]
  ) => void
  /** If the list should be scrollable */
  isScrollable?: boolean
  /** The number of columns to render */
  columns?: ColumnType
  /** The limit of filter results - if there is a limit the list will be expandable */
  limit?: number
  /** Showing show more in different design */
  showMoreRenderType?: ShowMoreRenderType
  /** The message to render in the button when the list is contracted */
  contractedMessage?: string
  /** Scrollable container max height */
  containerMaxHeight?: number
  /** Showing facets in different  design */
  renderType?: RenderType
  /** Showing facets in popover */
  isPopover?: boolean
  /** Whether there are applied filters or not */
  hasAppliedFilters: boolean
  /** Whether this filter is rendered in a BottomSheet, to apply specific styles */
  isBottomSheet?: boolean
}

const FacetFilterComponent: React.FC<FacetFilterProps> = props => {
  const {
    appliedFilters,
    facets,
    facetGroups,
    isScrollable = false,
    containerMaxHeight,
    renderType = RenderType.List,
    isPopover = false,
    hasAppliedFilters,
    isBottomSheet
  } = props
  const isSearchPending = useSelector(getIsSearchPending)

  const {formatMessage} = useIntl()

  const validFacets = useMemo(
    () => getValidFacets(facets, appliedFilters),
    [facets, appliedFilters]
  )

  if (validFacets.length === 0) {
    if (isSearchPending) {
      return <FacetFilterContentLoader renderType={renderType} />
    }
    return (
      <Box paddingBottom="s600">
        {formatMessage(filterMessages.noFiltersAvailable)}
      </Box>
    )
  }

  // Check if the facets are valid and group them by group type
  const groups = getValidFacetGroups(validFacets, facetGroups)

  const popularFacets = groups?.popular
  const popularIsVisible =
    validFacets.length > LENGTH_FOR_POPULAR_LIST_DISPLAY &&
    popularFacets?.length > 0

  const popularCardsIsVisible =
    renderType === RenderType.Card &&
    validFacets.length > LENGTH_FOR_POPULAR_LIST_DISPLAY &&
    popularFacets?.length > 0

  if (renderType === RenderType.Group) {
    if (isScrollable) {
      return (
        <ScrollableListWrapper
          maxHeight={containerMaxHeight}
          renderType={renderType}
        >
          <FacetsRenderGroup {...props} groups={groups} />
        </ScrollableListWrapper>
      )
    }

    return <FacetsRenderGroup {...props} groups={groups} />
  }

  if (renderType === RenderType.Pills) {
    return (
      <ScrollableWrapper
        className={cx(
          isPopover && '--is-popover',
          isBottomSheet && '--is-bottom-sheet'
        )}
      >
        <FacetsRenderPills
          facet={props.searchParamName as keyof FilterUrlParams}
          filters={props.filters}
          appliedFilters={props.appliedFilters}
          toggleMultiFilter={props.onChange}
          isPopover={isPopover}
          hasAppliedFilters={hasAppliedFilters}
        />
      </ScrollableWrapper>
    )
  }

  if (renderType === RenderType.CheckboxList) {
    return (
      <ScrollableWrapper
        className={cx(
          isPopover && '--is-popover',
          isBottomSheet && '--is-bottom-sheet'
        )}
      >
        <FacetsRenderCheckboxList
          facet={props.searchParamName as keyof FilterUrlParams}
          filters={props.filters}
          appliedFilters={props.appliedFilters}
          toggleMultiFilter={props.onChange}
          isPopover={isPopover}
          hasAppliedFilters={hasAppliedFilters}
        />
      </ScrollableWrapper>
    )
  }

  if (popularCardsIsVisible) {
    if (isScrollable) {
      return (
        <ScrollableListWrapper
          maxHeight={containerMaxHeight}
          renderType={renderType}
        >
          <FacetsRenderCard
            popularFacets={popularFacets}
            validFacets={validFacets}
            {...props}
          />
        </ScrollableListWrapper>
      )
    }
    return (
      <FacetsRenderCard
        popularFacets={popularFacets}
        validFacets={validFacets}
        {...props}
      />
    )
  }

  if (isScrollable) {
    return (
      <ScrollableListWrapper
        maxHeight={containerMaxHeight}
        renderType={renderType}
      >
        <FacetsRenderList
          popularIsVisible={popularIsVisible}
          popularFacets={popularFacets}
          validFacets={validFacets}
          {...props}
        />
      </ScrollableListWrapper>
    )
  }

  return (
    <ListWrapper maxHeight={containerMaxHeight}>
      <FacetsRenderList
        popularIsVisible={popularIsVisible}
        popularFacets={popularFacets}
        validFacets={validFacets}
        {...props}
      />
    </ListWrapper>
  )
}

export const FacetFilter = memo(FacetFilterComponent)
